import { Link } from "react-router-dom";
import { Shield, FileText, Mail, MapPin, CreditCard, Zap, ChevronRight, CheckCircle2, Flame, Leaf, ClipboardCheck } from "lucide-react";
import { LOGO_URL } from "@/config/branding";

export default function LandingPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F7' }} data-testid="landing-page">
      {/* Nav */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-[#E5E5EA] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="Self Certify UK" className="h-12 sm:h-14 object-contain object-left" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/contact" className="text-sm font-medium hover:text-[#00A3E0] transition-colors" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }} data-testid="nav-contact">
              Contact
            </Link>
            <Link to="/login" className="text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:bg-gray-100" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }} data-testid="nav-login">
              Sign In
            </Link>
            <Link to="/register" className="bg-[#00A3E0] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-[#0082B3] transition-all" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-register">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="auth-hero">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-5">
                <Zap className="w-4 h-4 text-[#D4A537]" />
                <span className="text-sm font-semibold text-[#D4A537]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  For Qualified Trades — Electricians, Gas Engineers & PAT Testers
                </span>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 text-sm font-semibold text-white">
                  <CheckCircle2 className="w-4 h-4 text-[#22C55E]" /> Free to Register
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 text-sm font-semibold text-white">
                  <CreditCard className="w-4 h-4 text-[#D4A537]" /> Pay As You Go
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif', lineHeight: 1.15 }}>
                Part P Building Notifications<br />
                Electrical Certificates<br />
                Gas / Boiler Certificates<br />
                MCS Compliance Documents<br />
                PAT Testing Reports<br />
                <span className="text-[#D4A537]">All Made EASY!</span>
              </h1>
              <p className="text-base sm:text-lg text-white/70 mb-10 max-w-lg leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Free to register — pay as you go certificates and Part P notifications. Everything you need in one platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/register" className="bg-[#D4A537] text-white text-center font-semibold px-8 py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#B8892C] transition-all text-base" data-testid="hero-cta" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Start Submitting <ChevronRight className="w-5 h-5" />
                </Link>
                <Link to="/contact" className="bg-white/10 backdrop-blur-sm text-white border border-white/20 text-center font-semibold px-8 py-3.5 rounded-xl hover:bg-white/20 transition-all text-base" data-testid="hero-contact" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Five Categories */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>
              Five Document Categories, One Platform
            </h2>
            <p className="text-sm sm:text-base max-w-lg mx-auto" style={{ color: '#8E8E93' }}>
              Everything a qualified tradesperson needs — all pay-as-you-go with branded PDFs
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: FileText, title: "Part P Notifications", desc: "Submit to local authorities with auto-generated certificates", color: "#007AFF", bg: "#E8F4FD", credits: "£4.99 each" },
              { icon: Zap, title: "Electrical Certs", desc: "EIC, EICR, Minor Works, Danger Notices and 12 more", color: "#00A3E0", bg: "#E8F7FC", credits: "20 for £5" },
              { icon: Flame, title: "Gas / Boiler", desc: "Safety records, commissioning, service reports and more", color: "#EF4444", bg: "#FEECEC", credits: "50 for £5" },
              { icon: Leaf, title: "MCS Compliance", desc: "Solar PV, battery, heat pump commissioning and handover", color: "#22C55E", bg: "#EAFBF0", credits: "20 for £5" },
              { icon: ClipboardCheck, title: "PAT Testing", desc: "Portable appliance testing with CSV bulk import", color: "#8B5CF6", bg: "#F3EFFE", credits: "10 for £5" },
            ].map((step, i) => (
              <div key={i} className="bg-[#F5F5F7] rounded-2xl p-5 text-center hover:shadow-md transition-all" data-testid={`category-card-${i}`}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: step.bg }}>
                  <step.icon className="w-7 h-7" style={{ color: step.color }} />
                </div>
                <h3 className="text-sm font-bold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>{step.title}</h3>
                <p className="text-xs leading-relaxed mb-3" style={{ color: '#8E8E93' }}>{step.desc}</p>
                <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ fontFamily: 'IBM Plex Mono, monospace', color: step.color, backgroundColor: step.bg }}>
                  {step.credits}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>
                Everything You Need<br />
                <span className="text-[#00A3E0]">In One Platform</span>
              </h2>
              <div className="space-y-4">
                {[
                  { title: "Part P Building Notifications", desc: "Submit to local authorities with auto-generated PDF certificates" },
                  { title: "15+ Electrical Certificates", desc: "EICR, EIC, Minor Works, Domestic EIC, Danger Notices and more" },
                  { title: "7 Gas / Boiler Certificates", desc: "Gas Safety Records, Commissioning Checklists, Service Reports" },
                  { title: "MCS Compliance Documents", desc: "Solar PV, Battery Storage, and Heat Pump commissioning" },
                  { title: "PAT Testing Reports", desc: "Portable appliance testing with CSV bulk import for large jobs" },
                  { title: "Postcode Lookup", desc: "Auto-fill addresses and local authority details from any UK postcode" },
                  { title: "Email to All Parties", desc: "Certificates emailed to the homeowner, technician, and local authority" },
                  { title: "Secure Payments", desc: "Simple and secure pay-as-you-go credit system via Stripe" },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#EAFBF0] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E]" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>{f.title}</p>
                      <p className="text-xs" style={{ color: '#8E8E93' }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-[#1C1C1E]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-base text-white/60 mb-8 max-w-lg mx-auto">
            Join electricians, heating engineers, and PAT testers across the UK who use Self Certify UK for fast, professional documentation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="bg-[#D4A537] text-white text-center font-semibold px-8 py-3.5 rounded-xl hover:bg-[#B8892C] transition-all text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create Free Account
            </Link>
            <Link to="/contact" className="bg-white/10 text-white border border-white/20 text-center font-semibold px-8 py-3.5 rounded-xl hover:bg-white/20 transition-all text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C1C1E] py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div>
              <img src={LOGO_URL} alt="Self Certify UK" className="h-10 object-contain mb-4" />
              <p className="text-xs text-white/40 leading-relaxed">
                Professional certificate and compliance documentation for UK electricians, gas engineers, and PAT testers.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4A537] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform</h4>
              <div className="flex flex-col gap-2">
                <Link to="/register" className="text-sm text-white/60 hover:text-white transition-colors">Register Free</Link>
                <Link to="/login" className="text-sm text-white/60 hover:text-white transition-colors">Sign In</Link>
                <Link to="/contact" className="text-sm text-white/60 hover:text-white transition-colors">Contact Us</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4A537] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Legal</h4>
              <div className="flex flex-col gap-2">
                <Link to="/privacy" className="text-sm text-white/60 hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/delete-account" className="text-sm text-white/60 hover:text-white transition-colors">Delete Account</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#D4A537] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Contact</h4>
              <a href="mailto:selfcertifyuk@gmail.com" className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                <Mail className="w-3.5 h-3.5" /> selfcertifyuk@gmail.com
              </a>
            </div>
          </div>
          <div className="pt-6 border-t border-white/10 text-center text-xs text-white/30">
            &copy; {new Date().getFullYear()} Self Certify UK. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
